// How a locked text box's type looks: bold, italic, underline, color, size
// and indent.
//
// CONSUMER: shared (the prep screen, the stamper, the layout and send
// storage, and node-pool tests).
//
// Nate, 2026-10-26, on the fixed wire-instruction text of the Commission
// Disbursement Authorization: "I want the ability to change the color,
// indentation, size, boldness, italicize, or even underline. This is a
// common capability in most other esign platforms."
//
// ── WHAT THIS IS, AND WHAT IT IS NOT ────────────────────────────────
//
// Only a box the SENDER fills in can carry a style: a locked text box,
// printed into the PDF's own bytes by document-stamping.ts. A signer's
// box is the signing service's, and the service decides how its type
// looks. So this is deliberately not a rich-text editor: one style per
// box, applied to all of its text, which is what "make this paragraph
// bold" means on a form.
//
// ── ONE VALIDATOR ───────────────────────────────────────────────────
//
// `parseTextStyle` is the only place a style is accepted from the
// outside (a request body, a stored JSON column). Everything it returns
// is inside the bounds below, and anything it cannot trust is dropped
// rather than clamped, the same rule `fontSize` already follows: the
// screen never showed a clamped value, so the page must not print one.
// A style with nothing set parses to null, so "no style" has exactly one
// spelling and an unstyled box stays byte-identical to what it was.
//
// Pure and dependency-free (the field-types.ts rule).

import { MAX_LINE_PITCH_PT } from "./multiline-text";

/** The colors a box may print in. A short, fixed list on purpose: this is
 *  a legal document, and any hex a request names could print type
 *  nobody can read. Navy and blue are the brand's; red is for a warning
 *  line ("Do not wire funds without calling to confirm"). */
export const TEXT_STYLE_COLORS = [
  { hex: "#000000", label: "Black" },
  { hex: "#022c51", label: "Navy" },
  { hex: "#3a7ea8", label: "Blue" },
  { hex: "#b42318", label: "Red" },
  { hex: "#4a4a4a", label: "Gray" },
] as const;

export const DEFAULT_TEXT_COLOR = "#000000";

/** Type sizes a fixed-text box may be set to, in points. */
export const TEXT_STYLE_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 24] as const;

/** Points a box's text may be moved in from its left edge, every line. */
export const MAX_TEXT_INDENT_PT = 72;
/** One press of the indent button. */
export const TEXT_INDENT_STEP_PT = 12;

export interface EsignTextStyle {
  bold?: true;
  italic?: true;
  underline?: true;
  /** One of TEXT_STYLE_COLORS. Absent means black. */
  color?: string;
  /** Points. Absent means the box's own size (the form's, or 10). */
  size?: number;
  /** Points in from the left edge, applied to every line. Absent means 0. */
  indent?: number;
}

/** The style a request or a stored column holds, validated; or null when
 *  it says nothing the page would show. Accepts the object or its JSON. */
export function parseTextStyle(raw: unknown): EsignTextStyle | null {
  let v: unknown = raw;
  if (typeof raw === "string") {
    if (raw.length === 0 || raw.length > 400) return null;
    try {
      v = JSON.parse(raw);
    } catch {
      // A hand-edited row reads as unstyled rather than taking down the
      // whole layout it sits in.
      return null;
    }
  }
  if (typeof v !== "object" || v === null) return null;
  const o = v as Record<string, unknown>;
  const out: EsignTextStyle = {};
  if (o.bold === true) out.bold = true;
  if (o.italic === true) out.italic = true;
  if (o.underline === true) out.underline = true;
  if (typeof o.color === "string") {
    const hex = o.color.toLowerCase();
    // Black is the default, so storing it would be a second spelling of
    // "unstyled".
    if (hex !== DEFAULT_TEXT_COLOR && TEXT_STYLE_COLORS.some((c) => c.hex === hex)) out.color = hex;
  }
  if (
    typeof o.size === "number" &&
    Number.isFinite(o.size) &&
    o.size >= Math.min(...TEXT_STYLE_SIZES) &&
    o.size <= Math.max(...TEXT_STYLE_SIZES)
  ) {
    out.size = o.size;
  }
  if (
    typeof o.indent === "number" &&
    Number.isFinite(o.indent) &&
    o.indent > 0 &&
    o.indent <= MAX_TEXT_INDENT_PT
  ) {
    out.indent = o.indent;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** The JSON a column stores, or null for an unstyled box. */
export function textStyleToJson(style: unknown): string | null {
  const parsed = parseTextStyle(style);
  return parsed ? JSON.stringify(parsed) : null;
}

/** The line pitch type of this size needs: a multi-line box prints at no
 *  more than three quarters of its pitch (multiline-text.ts), so the pitch
 *  grows with the type. Floored at 12 points, the pitch fixed text starts
 *  at, and capped at the largest pitch a box may have. */
export function pitchForTextSize(size: number): number {
  return Math.min(MAX_LINE_PITCH_PT, Math.max(12, Math.ceil(size / 0.75)));
}

/** The pitch a box moves to when its type size changes.
 *
 *  A box whose pitch is exactly what its OLD size implies is a freely sized
 *  one (fixed text), so it follows the new size both ways. Any other pitch
 *  is a form's printed rules: it only ever GROWS to fit bigger type, and is
 *  never tightened off the rules by a smaller one. */
export function pitchAfterSizeChange(currentPitch: number, oldSize: number, newSize: number): number {
  const wanted = pitchForTextSize(newSize);
  // A multi-line box never prints above three quarters of its pitch, so
  // that, not the nominal size, is the old size to compare against.
  const printedOld = Math.min(oldSize, currentPitch * 0.75);
  if (currentPitch === pitchForTextSize(printedOld)) return wanted;
  return Math.max(currentPitch, wanted);
}

/** "#022c51" to 0..1 channels, for the stamper. */
export function hexToUnitRgb(hex: string): { r: number; g: number; b: number } {
  const h = /^#[0-9a-f]{6}$/i.test(hex) ? hex : DEFAULT_TEXT_COLOR;
  return {
    r: Number.parseInt(h.slice(1, 3), 16) / 255,
    g: Number.parseInt(h.slice(3, 5), 16) / 255,
    b: Number.parseInt(h.slice(5, 7), 16) / 255,
  };
}
